import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StoreProjectAssigneeCreateForm } from "@/types/storeProjectAssignees";
import PageHeader from "@/components/PageHeader";
import { useStoreProjectAssigneeBreadcrumbs } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssigneeBreadcrumbs";
import {
  storeProjectAssigneeCreateForm,
  storeProjectAssigneeCreate,
} from "@/schemas/storeProjectAssignees";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useCreateRowSelectionStore } from "@/components/data-table/hooks/useCreateRowSelectionStore";
import Form from "@/components/Form";
import PageContent from "@/components/PageContent";
import { StoreProjectAssigneesCreatorSaveButton } from "./StoreProjectAssigneesCreatorSaveButton";
import { StoreProjectAssigneesCreatorStores } from "./StoreProjectAssigneesCreatorStores";
import { isRequired } from "@/lib/form";
import CheckboxField from "@/components/CheckboxField";
import UserSelect from "@/components/UserSelect";
import Field from "@/components/Field";
import { useCreateStoreProjectAssignee } from "@/modules/storeProjectAssignees/hooks/useCreateStoreProjectAssignee";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckedState } from "@radix-ui/react-checkbox";
import { SystemRoleCodesType } from "@/types/user";
import { ALLOWED_ROLES_FOR_ASSIGNMENTS } from "@/modules/defaultStoreProjectAssignees/constants";
import { isEmptyArray } from "@/lib/utils/isEmptyArray";
import { Module } from "@/constants";
import ProjectSelect from "@/components/ProjectSelect";

const defaultValues: DefaultValues<StoreProjectAssigneeCreateForm> = {
  project_id: "",
  is_primary: false,
  user_id: "",
  selectedSystemRoleCodes: ALLOWED_ROLES_FOR_ASSIGNMENTS,
};

export default function StoreProjectAssigneesCreator() {
  const { t } = useTranslation();
  const storesStore = useCreateRowSelectionStore();

  const breadcrumbItems = useStoreProjectAssigneeBreadcrumbs([
    {
      label: t("storeProjectAssignees.title.add"),
    },
  ]);
  const { can_create } = useModulePermissions(Module.StoreProjectAssignees);
  const mutation = useCreateStoreProjectAssignee();

  const isDisabled = mutation.isPending || !can_create;

  const form = useForm<StoreProjectAssigneeCreateForm>({
    resolver: zodResolver(storeProjectAssigneeCreateForm),
    defaultValues,
    disabled: isDisabled,
  });

  useFormUnsavedChangesWarning(form);

  const projectId = useWatch({ control: form.control, name: "project_id" });
  const selectedSystemRoleCodes = useWatch({
    control: form.control,
    name: "selectedSystemRoleCodes",
  });

  // During project change, clear the selected stores
  const prevProjectIdRef = useRef<string>("");
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      if (projectId) storesStore.clear();
    }
  }, [projectId, storesStore]);

  const supportedRoles = useMemo(
    () =>
      ALLOWED_ROLES_FOR_ASSIGNMENTS.map((systemRoleCode) => ({
        value: systemRoleCode,
        label: t(`userRole.${systemRoleCode}`),
      })),
    [t],
  );

  const handleRoleChange = useCallback(
    (role: SystemRoleCodesType, checked: CheckedState) => {
      const current = form.getValues(
        "selectedSystemRoleCodes",
      ) as SystemRoleCodesType[];
      let updated: SystemRoleCodesType[];
      if (checked) {
        updated = current.includes(role) ? current : [...current, role];
      } else {
        updated = current.filter((r) => r !== role);
      }
      form.setValue("selectedSystemRoleCodes", updated, {
        shouldValidate: false,
      });
    },
    [form],
  );

  const handleSubmit = useCallback(
    async (data: StoreProjectAssigneeCreateForm) => {
      const payload = storeProjectAssigneeCreate.parse({
        ...data,
        store_ids: Array.from(storesStore.getSelection()),
      });
      await mutation.mutateAsync(payload);
    },
    [mutation, storesStore],
  );

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Form form={form} handleSubmit={handleSubmit}>
        <PageContent
          title={t("storeProjectAssignees.title.add")}
          footer={
            <StoreProjectAssigneesCreatorSaveButton storesStore={storesStore} />
          }
        >
          <div className="flex flex-row gap-4 my-6">
            <Field
              name="project_id"
              label={t("storeProjectAssignees.fields.project_name")}
              required={isRequired(
                storeProjectAssigneeCreateForm.shape.project_id,
              )}
              render={({ field }) => (
                <ProjectSelect {...field} className="w-80" />
              )}
            />
          </div>

          <div className="flex flex-row gap-4 mt-6">
            <div className="text-sm">
              {t("storeProjectAssignees.title.role")}
            </div>
            {supportedRoles.map((role) => (
              <Label
                className="flex items-center gap-2 font-normal"
                key={role.value}
              >
                <Checkbox
                  value={role.value}
                  onCheckedChange={(checked) =>
                    handleRoleChange(role.value, checked)
                  }
                  checked={
                    selectedSystemRoleCodes?.includes(role.value) ?? false
                  }
                />
                {role.label}
              </Label>
            ))}
          </div>

          <div className="flex flex-row gap-4 my-6">
            <Field
              name="user_id"
              label={t("storeProjectAssignees.fields.user_name")}
              required={isRequired(
                storeProjectAssigneeCreateForm.shape.user_id,
              )}
              render={({ field }) => (
                <UserSelect
                  {...field}
                  className="w-80"
                  enabled={!isEmptyArray(selectedSystemRoleCodes)}
                  systemRoleCodes={selectedSystemRoleCodes ?? []}
                  disabled={
                    field.disabled || isEmptyArray(selectedSystemRoleCodes)
                  }
                  showRole={true}
                />
              )}
            />
            <CheckboxField
              name="is_primary"
              label={t("storeProjectAssignees.fields.is_primary")}
              required={isRequired(
                storeProjectAssigneeCreateForm.shape.is_primary,
              )}
            />
          </div>

          <div className="mt-6">
            <StoreProjectAssigneesCreatorStores
              projectId={projectId}
              store={storesStore}
            />
          </div>
        </PageContent>
      </Form>
    </>
  );
}
